import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2 } from 'lucide-react';

export type WaitlistPlan = { role: 'broker' | 'carrier'; cycle: 'monthly' | 'yearly' };

export default function FullAccessWaitlistModal({
  plan, onClose,
}: { plan: WaitlistPlan | null; onClose: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', website: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => { onClose(); setTimeout(() => { setDone(false); setError(null); }, 200); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plan) return;
    setError(null);
    if (!form.name.trim() || !form.email.trim() || form.phone.replace(/\D/g, '').length < 10) {
      setError('Please enter your name, email and a valid phone number.');
      return;
    }
    setLoading(true);
    const { data, error: err } = await supabase.functions.invoke('submit-broker-waitlist', {
      body: { ...form, role: plan.role, billing_cycle: plan.cycle },
    });
    setLoading(false);
    if (err || (data as any)?.error) {
      setError((data as any)?.error || 'Something went wrong. Please try again.');
      return;
    }
    setDone(true);
    setForm({ name: '', email: '', phone: '', message: '', website: '' });
  };

  return (
    <Dialog open={!!plan} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="sm:max-w-md">
        {done ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-primary mb-3" />
            <p className="text-lg font-semibold text-foreground">You're on the list — we'll be in touch.</p>
            <Button className="mt-5" onClick={close}>Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Full Access is currently invite-only</DialogTitle>
              <DialogDescription>
                LaneRunner is built on a fair, balanced marketplace — we're focused on matching capacity to the
                freight on our board so every broker has a real shot at winning loads. We've temporarily paused new
                Full Access memberships while we onboard more shippers and freight. Join the waitlist and we'll
                reach out the moment a spot opens.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 pt-2">
              <Input placeholder="Name *" maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input type="email" placeholder="Email *" maxLength={255} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input type="tel" placeholder="Phone *" maxLength={25} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <Textarea placeholder="Anything you'd like us to know?" maxLength={1000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Submitting…' : 'Join the Waitlist'}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
